import { getGoToTimeStreamRange } from '@app/modules/logger/screens/stream/hooks/cursor/get-go-to-time-stream-range';
import { useLoggerParams } from '@app/modules/logger/screens/stream/hooks/init/useLoggerParams';
import {
  useSetCursor,
  useSetFocusAtTs,
  useStreamUserDefinedRange,
} from '@app/modules/logger/screens/stream/stores/stream-screen.store';
import debugFn from 'debug';
import { useCallback } from 'react';
import { useStationStreamableRange } from '../range/useStationStreamableRange';

const debug = debugFn('useGoToTime');

export type UseGoToTimeOptions = {
  focusOffsetMs?: number;
};

/**
 * Reusable hook to navigate the stream to a given timestamp.
 * If the time is inside the current stream range, sets cursor and focus in place (store only, no URL update).
 * Otherwise updates URL with new range and cursor (clamped to station streamable range); useInitializeStream syncs URL to store and centers focus.
 */
export function useGoToTime({ focusOffsetMs = 0 }: UseGoToTimeOptions = {}) {
  const [, setUrlParams] = useLoggerParams();
  const userRange = useStreamUserDefinedRange();
  const streamableRange = useStationStreamableRange();
  const setCursor = useSetCursor();
  const setFocusAtTs = useSetFocusAtTs();

  const goToTime = useCallback(
    (startTs: number, endTs?: number) => {
      const isTargetTsInsideLoadedRange =
        startTs >= userRange.from && startTs <= userRange.to && (!endTs || endTs <= userRange.to);

      if (isTargetTsInsideLoadedRange) {
        debug('targetTs %s is inside loaded range, setting cursor only', startTs);
        setCursor(startTs);
        setFocusAtTs(Math.max(startTs - focusOffsetMs, userRange.from));
        return;
      }

      const { from, to } = getGoToTimeStreamRange({
        ts: startTs,
        streamableRange,
      });

      debug('targetTs %s is outside loaded range, setting range and cursor', startTs);
      setUrlParams({ from, to, cursor: startTs });
    },
    [streamableRange, setUrlParams, setCursor, userRange],
  );

  return { goToTime };
}
